/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useMemo } from 'react';
import ReactModal from 'react-modal';
import { useDispatch } from 'react-redux';
import { css, OpenCSSProperties, StyleSheet } from 'aphrodite';
import { colors as CCColors, mixins, IconButton, fonts } from '@blend/consumer-components';
import { Orientations } from '../../../../common/responsive';
import { setSelectedDocument } from '../../../../actions/documentActions';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { useConsumerTheme } from '../../../../hooks/useConsumerTheme';
import { zIndex } from '../../../../style/zIndex';
import { Document } from '../../Documents/Document';
import { getSelectedDocumentPdfUrl } from '@utils/documents';

export namespace DocumentsModal {
  export interface Props {
    isOpen: boolean;
    handleClose: () => void;
  }
}
const styles = StyleSheet.create({
  content: {
    height: '100%',
    backgroundColor: CCColors.white,
    display: 'flex',
    flexDirection: 'column',
  },
  headerWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    ...mixins.padding(16),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
  },
  header: {
    fontSize: 18,
    fontWeight: fonts.weight.medium,
  },
  documentsWrapper: {
    overflow: 'scroll',
  },
  document: {
    padding: 16,
  },
});

// TODO: handle slide up animation
// export for test use only
export const modalStyle: { [key: string]: OpenCSSProperties } = {
  content: {
    position: 'absolute',
    margin: 0,
    padding: 0,
    zIndex: zIndex.documentModal,
    width: '100%',
    height: '75%',
    top: 'auto',
    right: 'auto',
    bottom: '0',
    left: 'auto',
  },
  overlay: {
    backgroundColor: CCColors.contentOverlayBg,
    position: 'absolute',
    zIndex: zIndex.contentOverlay,
  },
  portraitOverlay: {
    top: '13%',
    right: '0px',
    bottom: '52px',
    left: '0px',
  },
  landscapeOverlay: {
    top: '0px',
    right: '20%',
    bottom: '0px',
    left: '52px',
  },
};

export const DocumentsModal: React.FC<DocumentsModal.Props> = ({ isOpen, handleClose }) => {
  const dispatch = useDispatch();
  const {
    callDetails,
    document: { documents, selectedDoc },
    layout: { orientation },
  } = useRootSelector(state => state);
  const {
    appConfig: { colors },
  } = useConsumerTheme();

  const modalStyleWithOrientation = useMemo(
    () => ({
      content: modalStyle.content,
      overlay: {
        ...modalStyle.overlay,
        ...(orientation === Orientations.portrait ? modalStyle.portraitOverlay : modalStyle.landscapeOverlay),
      },
    }),
    [orientation],
  );

  const setSelectedDocumentAndFetchPdf = useCallback(
    async (documentId: string) => {
      const fileUrl = await getSelectedDocumentPdfUrl(documentId, callDetails, documents, false);
      setSelectedDocument({
        id: documentId,
        pdfUrl: fileUrl,
      })(dispatch);
      handleClose();
    },
    [callDetails, documents, dispatch, handleClose],
  );
  return (
    <ReactModal
      contentLabel={'All Documents'}
      isOpen={isOpen}
      onRequestClose={handleClose}
      shouldFocusAfterRender
      shouldReturnFocusAfterClose
      style={modalStyleWithOrientation}
      overlayClassName="DocumentsModal-overlay"
      data-it="DocumentsModal"
    >
      <div id="modal-content" className={css(styles.content)}>
        <header className={css(styles.headerWrapper)}>
          <h2 className={css(styles.header)}>All Documents</h2>
          <IconButton onClick={handleClose} actionColor={colors.action} color={'action'} icon="delete" iconSize="md" />
        </header>
        <div className={css(styles.documentsWrapper)}>
          {documents.map(d => {
            const isDocumentSelected = selectedDoc?.id === d.id;
            return (
              <Document
                data-it="DocumentItem"
                key={d.id}
                document={d}
                isSelected={isDocumentSelected}
                disabled={isDocumentSelected}
                handleClick={setSelectedDocumentAndFetchPdf}
                styleOverrides={styles.document}
              />
            );
          })}
        </div>
      </div>
    </ReactModal>
  );
};
